import { useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { kpiSlice, DEFAULT_KPI_STATE, KPI_PREFERENCE_SUBTYPE_BASE, selectKPIPanel, type SelectedGroup } from "connected-components/dashboard/store/kpi-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"
import { getToday } from "helpers/types/dates"
import { resolveDateOverride, getEffectiveLatestDate, type DateOverride } from "connected-components/dashboard/helpers/date-override"

// Pure slice reader — does NOT sync preferences (KPIWrapper calls useSyncKPIPreferences once).
// Pass initialTab/initialDateOverride only from the wrapper; the seed is idempotent, so no-arg
// calls from children are safe.
export function useKPIData(initialTab?: string, initialDateOverride?: DateOverride | null) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(KPI_PREFERENCE_SUBTYPE_BASE)

  const kpiState = useAppSelector((s) => selectKPIPanel(s, dashboardId, panelKey) ?? DEFAULT_KPI_STATE)

  // Layout effect (registered before the sync's usePreferences) so the seed wins even when a
  // cache-warm fallback resolves synchronously on mount and omits selectedTab.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Either field alone latches — they're independent per the public signature.
    if (initialTab == null && initialDateOverride === undefined) return
    seeded.current = true
    dispatch(kpiSlice.actions.initFromModuleConfig({
      dashboardId,
      panelId: panelKey,
      selectedTab: initialTab,
      dateOverride: initialDateOverride,
    }))
  }, [dashboardId, panelKey, dispatch, initialTab, initialDateOverride])

  const kpiUpdaters = useMemo(() => ({
    setSectionExpanded: ({ key, value }: { key: string, value: unknown }) =>
      dispatch(kpiSlice.actions.setSectionExpanded({ dashboardId, panelId: panelKey, key, value })),
    setSelectedGroup: (group: SelectedGroup | null) =>
      dispatch(kpiSlice.actions.setSelectedGroup({ dashboardId, panelId: panelKey, group })),
    setSelectedTab: (tab: string) =>
      dispatch(kpiSlice.actions.setSelectedTab({ dashboardId, panelId: panelKey, tab })),
    setDateOverride: (dateOverride: DateOverride) =>
      dispatch(kpiSlice.actions.setDateOverride({ dashboardId, panelId: panelKey, dateOverride })),
    clearDateOverride: () =>
      dispatch(kpiSlice.actions.clearDateOverride({ dashboardId, panelId: panelKey })),
  }), [dispatch, dashboardId, panelKey])

  // Panel-level override supersedes the dashboard's dates when present; see use-graph-data.ts for
  // why resolveDateOverride re-derives from earliestDate/latestDate on every read.
  const effectiveDates = useMemo(() => {
    const dateOverride = kpiState.dateOverride
    const effectiveLatestDate = getEffectiveLatestDate(core.isIntraday, core.endDate, core.latestDate)
    const resolved = dateOverride ? resolveDateOverride(dateOverride, core.earliestDate, effectiveLatestDate) : null
    const endDate = resolved?.endDate ?? core.endDate
    return {
      startDate: resolved?.startDate ?? core.startDate,
      endDate,
      performanceStartDate: resolved?.performanceStartDate ?? core.performanceStartDate,
      performanceEndDate: resolved?.endDate ?? core.performanceEndDate,
      // Performance/Decisions branch on period === ONE_DAY to pick apiStartDate; leaving the
      // dashboard's raw period here would collapse an active multi-day override to a single day
      // whenever the dashboard itself is on "1D".
      period: resolved != null && dateOverride != null ? dateOverride.period : core.period,
      // A stored override can still resolve to null (boundaries not loaded yet, or a collapsed
      // fixed-length window) — callers gate "is an override actually in effect" on this, not on
      // the raw dateOverride, since dates fall back to core's in that case.
      isDateOverrideActive: resolved != null,
      // core.isIntraday only means "live" when the panel's own effective endDate (post override)
      // is today — a historical single-day override must not route through the live path just
      // because the dashboard itself is intraday.
      isIntradayMode: core.isIntraday && endDate === getToday(),
    }
  }, [kpiState.dateOverride, core.isIntraday, core.earliestDate, core.latestDate, core.startDate, core.endDate, core.performanceStartDate, core.performanceEndDate, core.period])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...kpiState,
    ...effectiveDates,
    ...kpiUpdaters,
  }), [core, modelId, trade, kpiState, effectiveDates, kpiUpdaters])
}
